import { useState, useEffect, useCallback, useRef } from 'react';
import { useSignIn } from 'react-auth-kit';
import Axios from 'axios';
import jwt_decode from 'jwt-decode';
import { getAuthHeader } from './authToken';

const STORAGE_KEY = 'session_expiry_at';
const CHECK_INTERVAL_MS = 60 * 1000; // check every 60s
const WARNING_BEFORE_MS = 2 * 60 * 60 * 1000; // warn 2 hours before
const GRACE_PERIOD_MS = 5 * 60 * 1000; // 5 minutes after expiry before auto-signout

/**
 * Returns the current session expiry state and a renewal function.
 *
 * expiryState:
 *   'ok'      — session is valid, no warning needed
 *   'warning' — session expires within 2 hours
 *   'expired' — session has expired, grace period running
 *   'signout' — grace period exhausted, should redirect to /signin
 */
export const useSessionExpiry = () => {
    const signIn = useSignIn();
    const [expiryState, setExpiryState] = useState('ok');
    const [minutesRemaining, setMinutesRemaining] = useState(null);
    const intervalRef = useRef(null);

    const computeState = useCallback(() => {
        const expiryStr = localStorage.getItem(STORAGE_KEY);
        if (!expiryStr) { setExpiryState('ok'); return; }

        const expiryAt = parseInt(expiryStr, 10);
        const now = Date.now();
        const diff = expiryAt - now;

        setMinutesRemaining(Math.max(0, Math.ceil(diff / 60000)));

        if (diff > WARNING_BEFORE_MS) {
            setExpiryState('ok');
        } else if (diff > 0) {
            setExpiryState('warning');
        } else if (Math.abs(diff) < GRACE_PERIOD_MS) {
            setExpiryState('expired');
        } else {
            setExpiryState('signout');
        }
    }, []);

    useEffect(() => {
        computeState();
        intervalRef.current = setInterval(computeState, CHECK_INTERVAL_MS);
        return () => clearInterval(intervalRef.current);
    }, [computeState]);

    const renewSession = useCallback(async () => {
        const authHeader = getAuthHeader();
        if (!authHeader) throw new Error('No auth token');

        const res = await Axios.post(
            `${process.env.REACT_APP_URL}/api/v1/renew`,
            {},
            { headers: { Authorization: authHeader } }
        );

        const { token, expiresIn, userName } = res.data;
        const decoded = jwt_decode(token);

        signIn({
            token,
            expiresIn,
            tokenType: 'Bearer',
            authState: { id: decoded.id, email: decoded.email, who: decoded.who },
        });

        if (userName) localStorage.setItem('userName', userName);

        const newExpiry = Date.now() + expiresIn * 60 * 1000;
        localStorage.setItem(STORAGE_KEY, newExpiry.toString());

        computeState(); // immediately update state
    }, [signIn, computeState]);

    return { expiryState, minutesRemaining, renewSession };
};

/** Store expiry timestamp on login — call this from Signin.jsx after signIn() */
export const setSessionExpiry = (expiresInMinutes) => {
    const expiryAt = Date.now() + expiresInMinutes * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, expiryAt.toString());
};

/** Clear expiry on logout — call from all handleLogout functions */
export const clearSessionExpiry = () => {
    localStorage.removeItem(STORAGE_KEY);
};
